import { NEVER_DIRTY, OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { NotFound } from '@client/components/NotFound';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useClaimStatesCapability } from '@client/hooks/business/claim/useClaimStatesCapability';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { ProductTypeStatesEditor } from './ProductTypeStatesEditor';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/states/$id')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'States',
        },
    },
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    const {
        isEnabled: claimStatesEnabled,
        isPending: isCapabilityPending,
        error: capabilityError,
        refetch: refetchCapability,
    } = useClaimStatesCapability();
    const { id } = Route.useParams();
    const navigate = useNavigate();
    const { trpc } = useTrpc();
    const productTypeQuery = trpc.productType.getProductType.useQuery({ id });
    const { data: productType, isPending } = productTypeQuery;

    if (isCapabilityPending) {
        return <Loading />;
    }

    // Surface a failed capability read instead of masquerading it as a disabled
    // feature — a rejected query leaves `claimStatesEnabled` false, which would
    // otherwise render a misleading NotFound for an org where states may be on.
    if (capabilityError) {
        return (
            <InlineQueryError
                error={capabilityError}
                onRetry={() => void refetchCapability()}
                title="Couldn't check claim states availability"
            />
        );
    }

    if (!claimStatesEnabled) {
        return <NotFound />;
    }

    return (
        <PageController route={Route}>
            <OverlayPage
                isDirty={NEVER_DIRTY}
                loading={isPending}
                title={
                    isPending ? (
                        <Skeleton.Node active className="h-6 w-60" />
                    ) : productType ? (
                        `Claim states - ${productType.name}`
                    ) : (
                        'Claim states'
                    )
                }
                size="large"
                afterClose={() => navigate({ moveInMatches: -1 })}
            >
                {productTypeQuery.error ? (
                    <InlineQueryError
                        error={productTypeQuery.error}
                        onRetry={() => void productTypeQuery.refetch()}
                        title="Couldn't load this product type"
                    />
                ) : productType ? (
                    <ProductTypeStatesEditor productTypeId={id} />
                ) : null}
            </OverlayPage>
        </PageController>
    );
}
